import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { FiArrowLeft, FiMail, FiExternalLink, FiInbox, FiSend, FiSettings } from 'react-icons/fi';

const EmailPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">UC MERCED EMAIL</h1>
          <p className="text-gray-600 mt-2">Access your university email account</p>
        </div>

        {/* Email Overview */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FiMail className="text-uc-gold" size={32} />
            <h2 className="text-xl font-bold text-uc-navy">Email Overview</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <FiInbox className="text-uc-gold mx-auto mb-2" size={32} />
              <p className="text-gray-600 text-sm">Unread Messages</p>
              <p className="text-2xl font-bold text-uc-navy mt-1">12</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <FiSend className="text-uc-gold mx-auto mb-2" size={32} />
              <p className="text-gray-600 text-sm">Sent Today</p>
              <p className="text-2xl font-bold text-uc-navy mt-1">5</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <FiMail className="text-uc-gold mx-auto mb-2" size={32} />
              <p className="text-gray-600 text-sm">Storage Used</p>
              <p className="text-2xl font-bold text-uc-navy mt-1">45%</p>
            </div>
          </div>
        </div>

        {/* Email Address Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-uc-navy mb-4">Your Email Address</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Primary Email</p>
            <p className="text-lg font-mono text-uc-navy">dkraman@merced.edu</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-uc-navy mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiMail className="text-uc-gold" size={24} />
              <div className="text-left">
                <p className="font-semibold text-uc-navy">Compose Email</p>
                <p className="text-sm text-gray-600">Write a new message</p>
              </div>
            </button>
            <button className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiSettings className="text-uc-gold" size={24} />
              <div className="text-left">
                <p className="font-semibold text-uc-navy">Email Settings</p>
                <p className="text-sm text-gray-600">Configure preferences</p>
              </div>
            </button>
          </div>
        </div>

        {/* Access Outlook */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-4">
            <FiExternalLink className="text-blue-600 mt-1 flex-shrink-0" size={24} />
            <div className="flex-1">
              <h3 className="font-bold text-blue-900 mb-2">Access Outlook Web App</h3>
              <p className="text-blue-800 mb-4">
                For full email functionality, access your email through the Outlook Web App.
              </p>
              <a
                href="https://outlook.office365.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Open Outlook <FiExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-bold text-uc-navy mb-4">About UC Merced Email</h3>
          <div className="space-y-3 text-gray-700">
            <p>
              UC Merced provides all students with a Microsoft 365 email account. Features include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>50 GB mailbox storage</li>
              <li>Calendar and scheduling tools</li>
              <li>Microsoft Teams integration</li>
              <li>Mobile app access (iOS and Android)</li>
              <li>Spam and malware protection</li>
              <li>Email forwarding and aliases</li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              For technical support or questions about email, contact IT Support at it@ucmerced.edu.
              You can also set up email on your mobile device or desktop client.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmailPage;
